/**
 * Centralized Logger
 *
 * Simple logging utility that wraps the observability store.
 * Provides structured logging with automatic context enrichment.
 *
 * @module core/utils/logger
 *
 * @example
 * ```typescript
 * import { logger, createScopedLogger } from '@/core/utils/logger';
 *
 * // Global logger
 * logger.info('Application started');
 * logger.error('Something went wrong', new Error('Oops'));
 *
 * // Scoped logger for a module
 * const log = createScopedLogger('OrgStore');
 * log.debug('Loading workspaces', { count: 5 });
 * log.error('Failed to load', new Error('Network error'));
 *
 * // With operation context
 * const opLog = log.withContext({ operation: 'sync', workspaceId: '123' });
 * opLog.info('Sync started');
 * opLog.info('Sync completed');
 * ```
 */

// Lazy-load observabilityStore to avoid pulling entire app into chunked modules
// The import chain: observabilityStore -> zustand -> immer -> local-first -> dexie
// This was causing AI chunks to balloon to 1.6+ MB
let _observabilityModule: typeof import('../observability/observabilityStore') | null = null;
let _loadPromise: Promise<typeof import('../observability/observabilityStore')> | null = null;
let _loadFailed = false;

/**
 * Load the observability module with promise caching to prevent race conditions.
 * Multiple concurrent calls will share the same promise.
 */
function loadObservabilityModule(): Promise<typeof import('../observability/observabilityStore')> {
  if (_loadFailed) {
    return Promise.reject(new Error('Observability module previously failed to load'));
  }
  if (!_loadPromise) {
    _loadPromise = import('../observability/observabilityStore')
      .then((module) => {
        _observabilityModule = module;
        return module;
      })
      .catch((error) => {
        _loadFailed = true;
        _loadPromise = null;
        // Log error to console since observability isn't available
        console.error('[Logger] Failed to load observability module:', error);
        // Don't throw the error - just return a rejected promise that won't crash the app
        return Promise.reject(error);
      });
  }
  return _loadPromise;
}

function getObservabilityStore() {
  // Return cached module if available, otherwise null (will use console fallback)
  return _observabilityModule?.useObservabilityStore ?? null;
}

function getCreateObservabilityLogger() {
  return _observabilityModule?.createLogger ?? null;
}

// Defer loading observability module until first use to avoid React initialization issues
// loadObservabilityModule().catch(() => {
//   // Error already logged in loadObservabilityModule
// });

// Fallback console logger when observabilityStore isn't available
function consoleFallback(level: string, message: string, context?: unknown) {
  const fn = level === 'debug' ? console.debug :
             level === 'info' ? console.info :
             level === 'warn' ? console.warn :
             level === 'error' ? console.error : console.log;
  if (context) {
    fn(`[${level.toUpperCase()}]`, message, context);
  } else {
    fn(`[${level.toUpperCase()}]`, message);
  }
}

// ============================================================================
// TYPES
// ============================================================================

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'fatal';

export interface LogContext {
  [key: string]: unknown;
}

export interface Logger {
  debug: (message: string, context?: LogContext) => void;
  info: (message: string, context?: LogContext) => void;
  warn: (message: string, context?: LogContext) => void;
  error: (message: string, error?: Error | unknown, context?: LogContext) => void;
  fatal: (message: string, error?: Error | unknown, context?: LogContext) => void;
  withContext: (context: LogContext) => Logger;
}

// ============================================================================
// GLOBAL LOGGER
// ============================================================================

/**
 * Global application logger.
 *
 * @example
 * ```typescript
 * import { logger } from '@/core/utils/logger';
 *
 * logger.info('User logged in', { userId: '123' });
 * logger.error('Failed to save', new Error('Network error'), { entityId: '456' });
 * ```
 */
export const logger: Logger = {
  debug: (message, context) => {
    const store = getObservabilityStore();
    if (store) {
      store.getState().log('debug', message, context);
    } else {
      consoleFallback('debug', message, context);
    }
  },

  info: (message, context) => {
    const store = getObservabilityStore();
    if (store) {
      store.getState().log('info', message, context);
    } else {
      consoleFallback('info', message, context);
    }
  },

  warn: (message, context) => {
    const store = getObservabilityStore();
    if (store) {
      store.getState().log('warn', message, context);
    } else {
      consoleFallback('warn', message, context);
    }
  },

  error: (message, error, context) => {
    const err = error instanceof Error ? error : undefined;
    const ctx = {
      ...context,
      ...(error && !(error instanceof Error) ? { errorValue: error } : {}),
    };
    const store = getObservabilityStore();
    if (store) {
      store.getState().error(message, err, ctx);
    } else {
      consoleFallback('error', message, { ...ctx, error: err });
    }
  },

  fatal: (message, error, context) => {
    const err = error instanceof Error ? error : undefined;
    const ctx = {
      ...context,
      ...(error && !(error instanceof Error) ? { errorValue: error } : {}),
    };
    const store = getObservabilityStore();
    if (store) {
      store.getState().fatal(message, err, ctx);
    } else {
      consoleFallback('error', `[FATAL] ${message}`, { ...ctx, error: err });
    }
  },

  withContext: (baseContext) => createLoggerWithContext(baseContext),
};

// ============================================================================
// SCOPED LOGGER FACTORY
// ============================================================================

/**
 * Create a logger scoped to a specific module.
 *
 * @param module - Module name for context
 * @returns Logger instance with module context
 *
 * @example
 * ```typescript
 * const log = createScopedLogger('TaskStore');
 * log.info('Task created', { taskId: '123' });
 * // Output: [TaskStore] Task created { taskId: '123' }
 * ```
 */
export function createScopedLogger(module: string): Logger {
  // Lazy logger - don't access observabilityStore until methods are called
  // This prevents circular dependency when createScopedLogger is called at module level
  let obsLogger: Logger | null = null;
  let loadAttempted = false;

  const getObsLogger = () => {
    if (!obsLogger && !loadAttempted) {
      loadAttempted = true;
      try {
        // Try to load observability module asynchronously
        loadObservabilityModule()
          .then(() => {
            const createLogger = getCreateObservabilityLogger();
            if (createLogger) {
              obsLogger = createLogger(module);
            }
          })
          .catch(() => {
            // Silently fail - will use console fallback
          });
      } catch (_e) {
        // Circular dependency during initialization - use console fallback
        return null;
      }
    }
    return obsLogger;
  };

  // Scoped console fallback
  const scopedConsoleFallback = (level: string, message: string, context?: unknown) => {
    consoleFallback(level, `[${module}] ${message}`, context);
  };

  const scopedLogger: Logger = {
    debug: (message, context) => {
      const logger = getObsLogger();
      if (logger) {
        logger.debug(message, context);
      } else {
        scopedConsoleFallback('debug', message, context);
      }
    },
    info: (message, context) => {
      const logger = getObsLogger();
      if (logger) {
        logger.info(message, context);
      } else {
        scopedConsoleFallback('info', message, context);
      }
    },
    warn: (message, context) => {
      const logger = getObsLogger();
      if (logger) {
        logger.warn(message, context);
      } else {
        scopedConsoleFallback('warn', message, context);
      }
    },
    error: (message, error, context) => {
      const logger = getObsLogger();
      if (logger) {
        logger.error(message, error, context);
      } else {
        scopedConsoleFallback('error', message, context);
        if (error) {
          console.error('[ERROR DETAILS]', error);
        }
      }
    },
    fatal: (message, error, context) => {
      const logger = getObsLogger();
      if (logger) {
        logger.fatal(message, error, context);
      } else {
        scopedConsoleFallback('fatal', message, context);
        if (error) {
          console.error('[FATAL ERROR DETAILS]', error);
        }
      }
    },
    withContext: (baseContext) => createLoggerWithContext({ ...baseContext, module }),
  };

  return scopedLogger;
}

// ============================================================================
// CONTEXT LOGGER FACTORY
// ============================================================================

/**
 * Create a logger with preset context.
 */
function createLoggerWithContext(baseContext: LogContext): Logger {
  return {
    debug: (message, context) => {
      logger.debug(message, { ...baseContext, ...context });
    },
    info: (message, context) => {
      logger.info(message, { ...baseContext, ...context });
    },
    warn: (message, context) => {
      logger.warn(message, { ...baseContext, ...context });
    },
    error: (message, error, context) => {
      logger.error(message, error, { ...baseContext, ...context });
    },
    fatal: (message, error, context) => {
      logger.fatal(message, error, { ...baseContext, ...context });
    },
    withContext: (additionalContext) =>
      createLoggerWithContext({ ...baseContext, ...additionalContext }),
  };
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Set the minimum log level.
 *
 * @param level - Minimum level to log
 */
export function setLogLevel(level: LogLevel): void {
  const store = getObservabilityStore();
  if (store) store.getState().setLogLevel(level);
}

/**
 * Get the current log level.
 */
export function getLogLevel(): LogLevel {
  const store = getObservabilityStore();
  return store?.getState().logLevel ?? 'info';
}

/**
 * Enable or disable logging.
 */
export function setLoggingEnabled(enabled: boolean): void {
  const store = getObservabilityStore();
  if (store) store.getState().setEnabled(enabled);
}

/**
 * Clear all logs.
 */
export function clearLogs(): void {
  const store = getObservabilityStore();
  if (store) store.getState().clearLogs();
}

/**
 * Export all observability data.
 */
export function exportLogs(): {
  logs: Array<{ level: string; message: string; timestamp: Date; context?: LogContext }>;
  errors: Array<{ id: string; name: string; message: string; timestamp: Date }>;
} {
  interface StoredError { id: string; name: string; message: string; timestamp: Date }
  const state = (getObservabilityStore() as { getState: () => { logs: Array<{ level: string; message: string; timestamp: Date; context?: LogContext }>; errors: StoredError[] } })?.getState();
  return {
    logs: state?.logs ?? [],
    errors: (state?.errors ?? []).map((e: StoredError) => ({
      id: e.id,
      name: e.name,
      message: e.message,
      timestamp: e.timestamp,
    })),
  };
}

// ============================================================================
// PERFORMANCE HELPERS
// ============================================================================

/**
 * Time an operation and log the result.
 *
 * @param name - Operation name
 * @param fn - Function to time
 * @param context - Additional context
 *
 * @example
 * ```typescript
 * const result = await timeOperation('fetchWorkspaces', async () => {
 *   return await api.getWorkspaces();
 * });
 * ```
 */
export async function timeOperation<T>(
  name: string,
  fn: () => T | Promise<T>,
  context?: LogContext
): Promise<T> {
  const start = performance.now();
  logger.debug(`Starting: ${name}`, context);

  try {
    const result = await fn();
    const duration = performance.now() - start;

    logger.info(`Completed: ${name}`, {
      ...context,
      durationMs: Math.round(duration),
    });

    return result;
  } catch (error) {
    const duration = performance.now() - start;

    logger.error(`Failed: ${name}`, error, {
      ...context,
      durationMs: Math.round(duration),
    });

    throw error;
  }
}

/**
 * Create a timer for manual timing.
 *
 * @param name - Operation name
 *
 * @example
 * ```typescript
 * const timer = createTimer('complexOperation');
 * // ... do work
 * timer.mark('step1');
 * // ... more work
 * timer.end(); // Logs total time and all marks
 * ```
 */
export function createTimer(name: string): {
  mark: (label: string) => void;
  end: () => number;
} {
  const start = performance.now();
  const marks: Array<{ label: string; time: number }> = [];

  return {
    mark: (label) => {
      marks.push({ label, time: performance.now() - start });
    },
    end: () => {
      const duration = performance.now() - start;
      logger.debug(`Timer: ${name}`, {
        durationMs: Math.round(duration),
        marks: marks.map((m) => ({
          label: m.label,
          ms: Math.round(m.time),
        })),
      });
      return duration;
    },
  };
}

// ============================================================================
// BROWSER CONSOLE HELPERS
// ============================================================================

/**
 * Debug logging helpers for browser console.
 *
 * Usage from browser console:
 *   VibeCoder.debug.enable()   - Enable debug logging
 *   VibeCoder.debug.disable()  - Disable debug (restore to info)
 *   VibeCoder.debug.status()   - Show current log level
 *   VibeCoder.debug.setLevel('warn') - Set specific level
 */
if (typeof window !== 'undefined') {
  const debugHelpers = {
    /** Enable debug logging */
    enable: () => {
      setLogLevel('debug');
      console.log('%c[VibeCoder] Debug logging enabled', 'color: #10b981; font-weight: bold');
    },

    /** Disable debug logging (restore to info level) */
    disable: () => {
      setLogLevel('info');
      console.log('%c[VibeCoder] Debug logging disabled (level: info)', 'color: #6366f1; font-weight: bold');
    },

    /** Show current log level status */
    status: () => {
      const level = getLogLevel();
      const store = getObservabilityStore();
      const enabled = store?.getState().enabled ?? false;
      console.log(`%c[VibeCoder] Logging status:`, 'color: #6366f1; font-weight: bold');
      console.log(`  Level: ${level}`);
      console.log(`  Enabled: ${enabled}`);
      console.log(`\nAvailable levels: debug, info, warn, error, fatal`);
      console.log(`\nCommands:`);
      console.log(`  VibeCoder.debug.enable()  - Enable debug logging`);
      console.log(`  VibeCoder.debug.disable() - Disable debug (set to info)`);
      console.log(`  VibeCoder.debug.setLevel('warn') - Set specific level`);
    },

    /** Set a specific log level */
    setLevel: (level: LogLevel) => {
      const validLevels: LogLevel[] = ['debug', 'info', 'warn', 'error', 'fatal'];
      if (!validLevels.includes(level)) {
        console.error(`Invalid level: ${level}. Valid levels: ${validLevels.join(', ')}`);
        return;
      }
      setLogLevel(level);
      console.log(`%c[VibeCoder] Log level set to: ${level}`, 'color: #6366f1; font-weight: bold');
    },

    /** Clear all logs */
    clear: () => {
      clearLogs();
      console.log('%c[VibeCoder] Logs cleared', 'color: #6366f1; font-weight: bold');
    },

    /** Export logs for debugging */
    export: () => {
      const data = exportLogs();
      console.log('%c[VibeCoder] Exported logs:', 'color: #6366f1; font-weight: bold');
      console.log(data);
      return data;
    },
  };

  // Attach to window.VibeCoder namespace
  (window as unknown as { VibeCoder?: { debug?: typeof debugHelpers } }).VibeCoder = {
    ...((window as unknown as { VibeCoder?: object }).VibeCoder || {}),
    debug: debugHelpers,
  };
}
